/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Trash2, Settings } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { mutate } from 'swr';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useToast } from '../ui/use-toast';

import OperatorOrganisationsDialog from './OperatorOrganisationsDialog';

import { IWithPagination, UserDataWithRole, Role, OrganisationData, ROLES } from '@/commons/commons';
import Pagination from '@/components/list/Pagination';
import { useGet, useMutate } from '@/net/commons';
import { useUserContext } from '@/pages/Root';
import { cn } from '@/utils';

type MemberRowActionsProps = {
    member: UserDataWithRole;
    toggleRemove: () => void;
    onOperatorOrganisationsClick?: () => void;
};

const MemberRowActions = ({ member, toggleRemove, onOperatorOrganisationsClick }: MemberRowActionsProps) => {
    return (
        <AlertDialog>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-7 w-7 p-0 data-[state=open]:bg-muted">
                        <DotsHorizontalIcon className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[160px]">
                    {member.role === ROLES.OPERATOR && (
                        <DropdownMenuItem onClick={onOperatorOrganisationsClick}>
                            <div className="flex w-full justify-between items-center">
                                Manage organisations
                                <Settings size={14} />
                            </div>
                        </DropdownMenuItem>
                    )}
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem className="text-destructive">
                            <div className="flex w-full justify-between items-center">
                                Remove member
                                <Trash2 size={14} />
                            </div>
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Remove member</AlertDialogTitle>
                    <AlertDialogDescription>
                        Are you sure you want to remove <span className="font-semibold text-black">{member.email}</span>
                        ?
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={toggleRemove}>
                        Remove
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default function MembersTable() {
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;
    const pageSize = Number(searchParams.get('pageSize')) || 20;
    const { toast } = useToast();
    const [selectedOperator, setSelectedOperator] = useState<UserDataWithRole | null>(null);
    const { data: members } = useGet<IWithPagination<UserDataWithRole>>(
        `/bff/organisation/members?page=${page}&pageSize=${pageSize}`,
    );
    const { data: organisation } = useGet<OrganisationData>('/bff/organisation');
    const { trigger: removeMember } = useMutate('/bff/organisation/members/:id', {
        method: 'DELETE',
    });
    const { trigger: updateRole } = useMutate('/bff/organisation/members/:id', {
        method: 'PATCH',
    });
    const { id: userId, isAdmin } = useUserContext().user;

    const isOperatorEnabled = useMemo(() => {
        if (!organisation) return false;
        const env = process.env.NODE_ENV;
        if (env === 'development') {
            return organisation.name === 'test-organisation';
        } else if (env === 'staging' || env === 'production') {
            return organisation.name === 'heal';
        }
        return false;
    }, [organisation]);

    const shouldShowPagination = (members?.pagination?.total || 0) > pageSize;

    const onRemove = async (id: number) => {
        try {
            await removeMember({ idParam: id });
            mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/members'), undefined, {
                revalidate: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to remove member',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    const onRoleChange = async (id: number, newRole: Role) => {
        try {
            await updateRole({ idParam: id, role: newRole });
            mutate((key) => typeof key === 'string' && key.startsWith('/bff/organisation/members'), undefined, {
                revalidate: true,
            });
        } catch (error) {
            toast({
                title: 'Failed to update role',
                description: 'Please try again',
                variant: 'destructive',
            });
        }
    };

    const handleOperatorOrganisationsClick = (member: UserDataWithRole) => {
        setSelectedOperator(member);
    };

    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[35%]">User</TableHead>
                        <TableHead className="w-[25%]">Joined</TableHead>
                        <TableHead className="w-[20%]">Role</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {members?.data.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                                There are no items to display.
                            </TableCell>
                        </TableRow>
                    )}
                    {members?.data.map((row) => {
                        const fullName =
                            row.firstName || row.lastName ? `${row.firstName} ${row.lastName}`.trim() : null;
                        return (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            {fullName && (
                                                <span className="capitalize text-sm font-medium">{fullName}</span>
                                            )}
                                            {row.id === userId && (
                                                <Badge
                                                    variant="outline"
                                                    className="bg-accent text-accent-foreground px-1.5 py-[1px]"
                                                >
                                                    You
                                                </Badge>
                                            )}
                                        </div>
                                        <span className={cn(fullName ? 'text-xs' : 'text-sm font-medium')}>
                                            {row.email}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell className="text-xs">
                                    {new Date(row.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    {row.id === userId || !isAdmin ? (
                                        <Badge variant="outline" className="capitalize">
                                            {row.role}
                                        </Badge>
                                    ) : (
                                        <Select
                                            value={row.role}
                                            onValueChange={(value: Role) => onRoleChange(row.id, value)}
                                        >
                                            <SelectTrigger className="w-[100px]">
                                                <SelectValue placeholder="Select role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="admin">Admin</SelectItem>
                                                <SelectItem value="member">Member</SelectItem>
                                                {isOperatorEnabled && (
                                                    <SelectItem value="operator">Operator</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </TableCell>
                                <TableCell className="flex justify-end">
                                    {row.id !== userId && isAdmin && (
                                        <MemberRowActions
                                            member={row}
                                            toggleRemove={() => onRemove(row.id)}
                                            onOperatorOrganisationsClick={
                                                row.role === ROLES.OPERATOR
                                                    ? () => handleOperatorOrganisationsClick(row)
                                                    : undefined
                                            }
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {members?.pagination && shouldShowPagination && <Pagination {...members.pagination} />}

            <OperatorOrganisationsDialog operator={selectedOperator} onClose={() => setSelectedOperator(null)} />
        </>
    );
}
